import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { waUrl } from "@/lib/invite";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const NoInvites = ({ bono }) => (
  <div data-testid="invite-none-left" className="rounded-xl border border-[#bc9538]/50 bg-[#bc9538]/10 p-4 space-y-1.5">
    <p className="font-semibold flex items-center gap-1.5"><Gift className="w-4 h-4 c-accent" /> Ya no tienes invitaciones</p>
    <p className="text-sm c-text-2">Ganas {bono} invitaciones más por cada invitado que active su red (cuando carga 10 contactos clasificados).</p>
    <Link to="/conecta/invitaciones" className="text-sm font-semibold c-accent">Ver mis invitaciones</Link>
  </div>
);

const Step = ({ contact, onDone }) => {
  const [inv, setInv] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState(null);
  useEffect(() => {
    setInv(null); setErr(null);
    api.post("/red/invitaciones", { contact_id: contact.id })
      .then(({ data }) => { setInv(data); setMsg(data.mensaje); })
      .catch((e) => setErr(e.response?.status === 403 ? "none" : errMsg(e)));
  }, [contact.id]);
  if (err === "none") return <NoInvites bono={5} />;
  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!inv) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin c-muted" /></div>;
  const copy = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success("Mensaje con liga copiado"); onDone(); } catch { toast.error("No se pudo copiar"); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm">Para <span className="font-semibold" data-testid="invite-step-contact">{contact.name}</span></p>
      <textarea data-testid="invite-message-input" className="c-input min-h-[140px] text-sm" value={msg} onChange={(e) => setMsg(e.target.value)} />
      {inv.wa_phone ? (
        <a data-testid="invite-open-whatsapp" href={waUrl(inv.wa_phone, msg)} target="_blank" rel="noopener noreferrer" onClick={onDone}
          className="w-full py-3.5 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2"><ExternalLink className="w-4 h-4" /> Abrir WhatsApp</a>
      ) : (
        <button data-testid="invite-copy-link" onClick={copy} className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2"><Copy className="w-4 h-4" /> Sin teléfono: copiar liga</button>
      )}
    </div>
  );
};

export const InviteFlow = ({ contacts, open, onOpenChange }) => {
  const [i, setI] = useState(0);
  const [sent, setSent] = useState(false);
  useEffect(() => { if (open) { setI(0); setSent(false); } }, [open]);
  const c = contacts[i];
  const last = i >= contacts.length - 1;
  const next = () => { if (last) onOpenChange(false); else { setI(i + 1); setSent(false); } };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="invite-sheet" className="max-h-[92vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading">Invitar a CONECTA{contacts.length > 1 ? ` (${i + 1} de ${contacts.length})` : ""}</SheetTitle>
          <SheetDescription>Edita el mensaje antes de enviarlo. La liga lleva tu código.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 max-w-md mx-auto space-y-3">
          {c && <Step key={c.id} contact={c} onDone={() => setSent(true)} />}
          {(contacts.length > 1 || sent) && (
            <button data-testid="invite-next-btn" onClick={next} className="w-full py-3 rounded-xl c-card font-semibold">{last ? "Terminar" : "Siguiente"}</button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
